<template lang="pug">
  v-container
    v-row
      v-col.text-center
        v-card.d-inline-block
          v-card-title.text-caption.text-center.justify-center Cagnotte
          v-card-text.text-h2 {{ $money(walletAmount) }}
    v-row(v-if="errorMessage")
      v-col
        v-alert(
          :color="$theme.background.color"
          type="info") {{ errorMessage }}
    template(v-else)
      v-subheader.my-4 Saisissez vos coordonnées bancaires pour faire une demande de retrait.
      v-checkbox(
        v-model="remember"
        :input-value="remember"
        label="Se souvenir de moi")
      v-form(
        @submit="onSubmit"
        v-model="form")
        v-row
          v-col(:cols="6")
            v-text-field(
              v-model="surname"
              filled
              :rules="[value => !!value ||  `Le prénom est requis`]"
              label="Prénom")
          v-col(:cols="6")
            v-text-field(
              v-model="name"
              filled
              required
              :rules="[value => !!value ||  `Le nom est requis`]"
              label="Nom")
          v-col(:cols="12")
            input-iban(
              v-model="iban"
              filled
              :rules="[value => !!value ||  `L'IBAN est requis`]"
              label="IBAN")
          v-col(:cols="12")
            input-bic(
              v-model="bic"
              filled
              :rules="[value => !!value ||  `Le BIC est requis`]"
              label="BIC")
</template>

<script>
import illustration from '@iez/illustrations/Elements'
import { page } from '@iez/core'
import IllustrationState from '@iez/components/illustration-state/'
import { ref, computed, toRefs } from '@vue/composition-api'
import { injectRouteState } from '@iez/core'
import router from '@iez/core/router/'
import data from '@iez/lib/data/'
import waitFor from '@iez/lib/wait-for/'
import InputBic from '@iez/components/inputs/bic/'
import InputIban from '@iez/components/inputs/iban/'
import dayjs from 'dayjs'

const route = {
  name: `ReceivedSupports`,
  path: `/received-supports`
}

export default {
  ...route,

  components: {
    IllustrationState,
    InputBic,
    InputIban
  },

  setup () {
    const form = ref(false)

    const errorMessage = computed(
      () => lastRequestToWithdraw.value
        ? dayjs().diff(dayjs(lastRequestToWithdraw.value.createdAt), `month`) < 1
          ? `Vous avez déjà fait une demande ce mois-ci.`
          :  routeState.walletAmount <= 500
            ? `Vous devez avoir accumulé plus de 5,00€ avant de faire une demande.`
            : null
        : routeState.walletAmount <= 500
            ? `Vous devez avoir accumulé plus de 5,00€ avant de faire une demande.`
            : null
    )

    const me = data.me({
      fields: [
        `id`, {
          association: [
            `id`, {
              donations: [
                `id`, {
                  payment: [
                    `id`,
                    `amount`
                  ]
                }
              ],
              viewsAds: [
                `id`, {
                  ad: [
                    `id`,
                    `pricePerView`
                  ]
                }
              ],
              requestsToWithdraw: [
                `id`,
                `amount`,
                `amountForYako`
              ]
            }
          ]
        }
      ]
    })

    const routeState = injectRouteState({
      payload: {
        associationId: computed(() => me.value?.association?.id),

        viewsAdsAmount: computed(() =>
          me.value?.association?.viewsAds
            ?.reduce(
              (accumulator, { ad: { pricePerView } }) => accumulator + pricePerView,
              0
            ) / 100 ?? 0
        ),

        donationsAmount: computed(
          () => me.value?.association?.donations
            ?.reduce(
              (accumulator, { payment: { amount } }) => accumulator + amount,
              0
            ) ?? 0
        ),

        requestsToWithdrawAmount: computed(
          () => me.value?.association?.requestsToWithdraw
            ?.reduce(
              (accumulator, { amount, amountForYako }) => accumulator + amount + amountForYako,
              0
            ) ?? 0
        ),

        walletAmount: computed(
          () =>
            routeState.viewsAdsAmount +
            routeState.donationsAmount -
            routeState.requestsToWithdrawAmount
        )
      }
    })

    const lastRequestToWithdraw = data.get({
      root: `requestsToWithdraw`,
      one: true,
      request: () => ({
        where: {
          associationId: {
            _eq: routeState.associationId
          }
        },
        order_by: {
          createdAt: `desc`
        }
      }),
      fields: [
        `id`,
        `createdAt`,
        `name`,
        `surname`,
        `iban`,
        `bic`,
        `remember`
      ]
    })

    page.blockingProgression.set(
      () => lastRequestToWithdraw.value !== undefined
        ? null
        : {
          isActive: true
        }
    )

    const name = ref(routeState.name ?? ``)
    const surname = ref(routeState.surname ?? ``)
    const iban = ref(routeState.iban ?? ``)
    const bic = ref(routeState.bic ?? ``)
    const remember = ref(routeState.remember ?? false)

    waitFor({
      dependencies: () => lastRequestToWithdraw.value !== undefined
    }).then(() => {
      if (lastRequestToWithdraw.value?.remember) {
        name.value = lastRequestToWithdraw.value?.name
        surname.value = lastRequestToWithdraw.value?.surname
        iban.value = lastRequestToWithdraw.value?.iban
        bic.value = lastRequestToWithdraw.value?.bic
        remember.value = lastRequestToWithdraw.value?.remember
      }
    })

    page.topBar.set({
      title: `Mes soutiens`
    })

    const onSubmit = () => {
      routeState.name = name.value
      routeState.surname = surname.value
      routeState.iban = iban.value
      routeState.bic = bic.value
      routeState.remember = remember.value
      router.push({
        name: `ReceivedSupportsAmount`
      })
    }

    page.bottomAction.set(
      () => form.value
        ? {
          action: {
            onClick: onSubmit,
            label: `Valider`
          }
        } : null
    )

    return {
      illustration,
      form,
      name,
      surname,
      iban,
      bic,
      remember,
      onSubmit,
      ...toRefs(routeState),
      errorMessage,
      lastRequestToWithdraw,
      routeState
    }
  }
}
</script>
